// **The agent-prompt feed poller** (`match-hud` **D-7**), extracted from `app/PvpPromptFeed.tsx` so
// the Match screen's prompt cards and the floating aside share one poller rather than two racing
// each other with two cursors.
//
// **Scoping is unchanged and deliberate.** A player reads `/live/prompts`, which the server filters
// to the caller's own colony (`pvp_chat.rs`); the presenter reads `/presentation/prompts`, which
// carries both. That split is DESIGN §9's line in a different place from `/live/stats`: board state
// (population, composition, nectar, capacity) is open, authored content (a player's own words) is
// not. The privacy carve-out at `coordinator.rs` is preserved rather than reversed — so on a
// player's device the opposing panel simply has no prompt section.
//
// **Backwards paging is the cursor already on the route.** `?afterCursor=N` returns everything
// after `N`, the server retains 200, and this hook starts at 0 and accretes — so the history is
// however much the server still holds, not the last 12 (which is what both ends used to trim to).

import { useEffect, useState } from 'react';

export interface PromptEntry {
  cursor: number;
  colony: number;
  /** The sim tick the prompt was raised at — never a wall clock (DESIGN §7). */
  tick: number;
  /** `auto` | `human`. */
  author: string;
  name: string;
  text: string;
}

export type PromptFeedMode = 'player' | 'presenter';

export interface PromptFeedSeams {
  mode: PromptFeedMode;
  token?: string | null;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  pollMs?: number;
}

export function decodePromptFeed(
  value: unknown,
): { cursor: number; prompts: PromptEntry[] } | null {
  if (value === null || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (typeof row.cursor !== 'number' || !Array.isArray(row.prompts)) return null;
  const prompts = row.prompts.filter((entry): entry is PromptEntry => {
    if (entry === null || typeof entry !== 'object') return false;
    const prompt = entry as Record<string, unknown>;
    return (
      typeof prompt.cursor === 'number' &&
      typeof prompt.colony === 'number' &&
      typeof prompt.tick === 'number' &&
      typeof prompt.author === 'string' &&
      typeof prompt.name === 'string' &&
      typeof prompt.text === 'string'
    );
  });
  return { cursor: row.cursor, prompts };
}

/** Every prompt this caller may read, oldest first, accreted across polls. */
export function useAgentPromptFeed({
  mode,
  token = null,
  baseUrl = '',
  fetchImpl,
  pollMs = 1_500,
}: PromptFeedSeams): PromptEntry[] {
  const [prompts, setPrompts] = useState<PromptEntry[]>([]);

  useEffect(() => {
    if (token === null) {
      return undefined;
    }
    const doFetch = fetchImpl ?? globalThis.fetch?.bind(globalThis);
    if (doFetch === undefined) {
      return undefined;
    }
    let live = true;
    let cursor = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const path = mode === 'presenter' ? '/presentation/prompts' : '/live/prompts';
    const poll = async (): Promise<void> => {
      try {
        const response = await doFetch(
          `${baseUrl.replace(/\/+$/, '')}${path}?afterCursor=${cursor}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const decoded = response.ok ? decodePromptFeed(await response.json()) : null;
        if (live && decoded !== null) {
          cursor = Math.max(cursor, decoded.cursor);
          if (decoded.prompts.length > 0) {
            setPrompts((current) => [...current, ...decoded.prompts]);
          }
        }
      } catch {
        // A poll is advisory UI. Keep the last readable feed and try again.
      }
      if (live) timer = setTimeout(() => void poll(), pollMs);
    };
    void poll();
    return () => {
      live = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [baseUrl, fetchImpl, mode, pollMs, token]);

  return prompts;
}
